import React, { useEffect, useRef, useState } from 'react';
import './GameCanva.css';

const GameCanva = ({ onGameOver }) => {
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  
  const [score, setScore] = useState(0);
  const [nbBonus, setNbBonus] = useState(0);
  const [pulseBonus, setPulseBonus] = useState(false); 
  const [personalRecord] = useState(() => {
    return Number(localStorage.getItem('techrunner_record')) || 0;
  });
  
  const imagesRef = useRef({
    idle: new Image(), run1: new Image(), run2: new Image(),
    jump: new Image(), crouch: new Image(), death: new Image(), server: new Image(), 
    wifi: new Image(), barrier: new Image(), drone: new Image(), cable: new Image(),
    usb: new Image(), router: new Image(), shield: new Image()
  });

  useEffect(() => {
    imagesRef.current.idle.src = '/assets/hero_idle.png';
    imagesRef.current.run1.src = '/assets/hero_run1.png';
    imagesRef.current.run2.src = '/assets/hero_run2.png';
    imagesRef.current.jump.src = '/assets/hero_jump.png';
    imagesRef.current.death.src = '/assets/hero_death.png';
    imagesRef.current.crouch.src = '/assets/hero_crouch.png';
    imagesRef.current.server.src = '/assets/obs_server.png';
    imagesRef.current.barrier.src = '/assets/obs_barrier.png';
    imagesRef.current.cable.src = '/assets/obs_cable.png';
    imagesRef.current.wifi.src = '/assets/obs_wifi.png';
    imagesRef.current.drone.src = '/assets/obs_drone.png';
    imagesRef.current.router.src = '/assets/bonus_router.png';
    imagesRef.current.usb.src = '/assets/bonus_usb.png';
    imagesRef.current.shield.src = '/assets/bonus_shield.png';

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const video = videoRef.current;
    let animationFrameId;
    let uiUpdateInterval;

    let isPlaying = true;
    let currentScore = 0;
    let activePower = '';
    let timerActivePower = 0;
    let gameSpeed = 15;
    let obstacleGeneration = 1.5;
    let spawnTimer = 0;
    let bonusSpawnTimer = 0;
    let nb_bonus = 0;
    
    const gravity = 0.6;
    const groundLevelY = 200; 

    let player = {
      x: 80,
      y: groundLevelY,
      width: 50,  
      height: 70, 
      vY: 0,
      isJumping: false,
      isDoubleJumping: false,
      isCrouching: false,
      animFrame: 0, 
      animCounter: 0
    };

    const OBSTACLE_TYPES = [
      { type: 'ground', width: 45, height: 65, y: 205, sprite: imagesRef.current.server, name: 'Server' },
      { type: 'ground', width: 50, height: 40, y: 230, sprite: imagesRef.current.barrier, name: 'Barrier' },
      { type: 'ground', width: 50, height: 40, y: 230, sprite: imagesRef.current.cable, name: 'Cable' },
      { type: 'air', width: 45, height: 40, y: 120, sprite: imagesRef.current.wifi, name: 'Wifi' },
      { type: 'air', width: 55, height: 35, y: 90, sprite: imagesRef.current.drone, name: 'Drone' },
    ];

    const BONUS_TYPES = [
      { type: 'bonus', width: 30, height: 43, sprite: imagesRef.current.usb, name: 'USB', power: 'Pouvoir : Saut double' },
      { type: 'bonus', width: 30, height: 43, sprite: imagesRef.current.router, name: 'Router', power: 'Pouvoir : Score x2' },
      { type: 'bonus', width: 30, height: 43, sprite: imagesRef.current.shield, name: 'Shield', power: 'Pouvoir : Bouclier' }
    ];

    let obstacles = [];
    let bonuses = [];
    let crouchAngle = 0;

    // --- VARIABLES POUR BRIDER À 60 FPS ---
    let lastTime = performance.now();
    const fpsInterval = 1000 / 60; 

    if (video) {
      video.play().catch(err => console.log("Attente d'interaction utilisateur pour lancer la vidéo", err));
    }

    const triggerJump = () => {
      if (!isPlaying ) return;

      if (player.isJumping && activePower === 'Pouvoir : Saut double') {
        if (!player.isDoubleJumping) {
          player.vY = -13; 
          player.isDoubleJumping = true; 
        }
      } 
      else if (!player.isJumping) {
        player.vY = -13; 
        player.isJumping = true; 
      }
    };

    const handleTouchStart = (e) => { e.preventDefault(); triggerJump(); };
    const handleMouseDown = () => { triggerJump(); };

    const handleKeyDown = (e) => {
      if (['Space', 'ArrowUp', 'ArrowDown'].includes(e.code)) e.preventDefault();
      if (!isPlaying) return;

      if ((e.code === 'Space' || e.code === 'ArrowUp')) {
        triggerJump();
      }
      if (e.code === 'ArrowDown') {
        if (player.isJumping) player.vY = 15; 
        player.isCrouching = true;
      }
    };

    const handleKeyUp = (e) => {
      if (e.code === 'ArrowDown') player.isCrouching = false;
    };

    window.addEventListener('keydown', handleKeyDown, { passive: false });
    window.addEventListener('keyup', handleKeyUp);
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('mousedown', handleMouseDown);

    uiUpdateInterval = setInterval(() => {
      if (isPlaying) {
        setScore(currentScore);
        setNbBonus(nb_bonus);
      }
    }, 100);

    const getSafeSpawnX = (baseX, newY) => {
      let safeX = baseX;
      const minDistanceX = 160; 
      const minDistanceY = 60;  

      obstacles.forEach(obs => {
        if (Math.abs(newY - obs.y) < minDistanceY && Math.abs(safeX - obs.x) < minDistanceX) {
          safeX = obs.x + minDistanceX;
        }
      });
      return safeX;
    };

    const gameLoop = (currentTime) => {
      if (!isPlaying) return;

      animationFrameId = requestAnimationFrame(gameLoop);

      // Calcul de l'écart temporel réel entre deux rafraîchissements
      const elapsed = currentTime - lastTime;

      // Trop rapide (ex: écran 144Hz+) ? On intercepte et on bloque l'exécution
      if (elapsed < fpsInterval) return;

      // Réajustement de la marque temporelle fixe
      lastTime = currentTime - (elapsed % fpsInterval);

      // --- DEBUT DE LA LOGIQUE DE JEU COMPRESSIONEE A 60 FPS ---
      currentScore += (activePower === 'Pouvoir : Score x2') ? 2 : 1;
      if (activePower !== '') { timerActivePower++; }
      if (timerActivePower >= 300) { activePower = ''; timerActivePower = 0; }
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (video && video.readyState >= 2) {
        let temp = (activePower === 'Pouvoir : Score x2') ? 16 : gameSpeed / 5;
        video.playbackRate = (temp < 16) ? temp : 16;
        ctx.filter = 'blur(2px)';
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        ctx.filter = 'none';
      } else {
        ctx.fillStyle = '#1e293b';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      ctx.strokeStyle = '#00afb9';
      ctx.lineWidth = 3; 
      ctx.beginPath();
      ctx.moveTo(0, 270);
      ctx.lineTo(canvas.width, 270);
      ctx.stroke();

      player.vY += gravity;
      player.y += player.vY;

      if (player.y > groundLevelY) {
        player.y = groundLevelY;
        player.vY = 0;
        player.isJumping = false;
        if (activePower === 'Pouvoir : Saut double') { player.isDoubleJumping = false; }
      }

      let playerSprite = imagesRef.current.run1;
      if (player.isJumping) playerSprite = imagesRef.current.jump;
      else if (player.isCrouching) playerSprite = imagesRef.current.crouch;
      else {
        player.animCounter++;
        if (player.animCounter > 6) {
          player.animFrame = player.animFrame === 0 ? 1 : 0;
          player.animCounter = 0;
        }
        playerSprite = player.animFrame === 0 ? imagesRef.current.run1 : imagesRef.current.run2;
      }

      if (playerSprite && playerSprite.complete) {
        if (player.isCrouching && !player.isJumping) {
          crouchAngle += gameSpeed / 35;
          ctx.save(); 
          const renderSize = 45;  
          ctx.translate(player.x + renderSize / 2, 270 - (renderSize / 2)); 
          ctx.rotate(crouchAngle); 
          ctx.drawImage(playerSprite, -renderSize / 2, -renderSize / 2, renderSize, renderSize);
          ctx.restore(); 
        } else {
          crouchAngle = 0; 
          ctx.drawImage(playerSprite, player.x, player.y, player.width, player.height);
        }
      }

      let currentHitboxY = player.y;
      let currentHitboxHeight = player.height;
      if (player.isCrouching && !player.isJumping) {
        currentHitboxY = player.y + 35;      
        currentHitboxHeight = player.height - 35; 
      }

      spawnTimer++;
      if (spawnTimer > (Math.random() * 40 + 50) - obstacleGeneration) {
        const chosen = OBSTACLE_TYPES[Math.floor(Math.random() * OBSTACLE_TYPES.length)];
        let finalY = chosen.type === 'air' ? Math.floor(Math.random() * 120) + 60 : chosen.y;
        obstacles.push({
          x: getSafeSpawnX(canvas.width, finalY),
          y: finalY,
          width: chosen.width,
          height: chosen.height,
          type: chosen.type,
          name: chosen.name,
          sprite: chosen.sprite
        });
        spawnTimer = 0;
      }

      bonusSpawnTimer++;
      if (bonusSpawnTimer > Math.random() * 350 + 400) {
        const chosenBonus = BONUS_TYPES[Math.floor(Math.random() * BONUS_TYPES.length)];
        let finalBonusY = Math.floor(Math.random() * 120) + 60;
        bonuses.push({
          x: getSafeSpawnX(canvas.width, finalBonusY),
          y: finalBonusY,
          width: chosenBonus.width,
          height: chosenBonus.height,
          type: chosenBonus.type,
          name: chosenBonus.name,
          power: chosenBonus.power,
          sprite: chosenBonus.sprite
        });
        bonusSpawnTimer = 0; 
      }

      // --- FILTRAGE ET DEPLACEMENT DES OBSTACLES ---
      obstacles = obstacles.filter(obs => {
        obs.x -= gameSpeed;

        if (obs.x + obs.width < 0) {
          currentScore += (activePower === 'Pouvoir : Score x2') ? 200 : 100;
          gameSpeed += (gameSpeed <= 13) ? 0.15 : 1; 
          obstacleGeneration += (obstacleGeneration <= 20) ? 0.075 : 0;
          return false; 
        }

        if (obs.sprite?.complete) { 
          ctx.drawImage(obs.sprite, obs.x, obs.y, obs.width, obs.height);
        }

        if (
          player.x < obs.x + obs.width &&
          player.x + player.width > obs.x &&
          currentHitboxY < obs.y + obs.height &&
          currentHitboxY + currentHitboxHeight > obs.y
        ) {
          if (activePower === 'Pouvoir : Bouclier') {
            activePower = '';
            timerActivePower = 0;
            return false;
          } else {
            isPlaying = false;
            if (video) video.pause();
            if (imagesRef.current.death.complete) {
              ctx.drawImage(imagesRef.current.death, player.x, player.y + 20, player.height, player.width);
              const savedRecord = Number(localStorage.getItem('techrunner_record')) || 0;
              if (currentScore > savedRecord) {
                localStorage.setItem('techrunner_record', String(currentScore));
              }
            }
            setScore(currentScore);
            onGameOver(currentScore);
          }
        }
        return true;
      });

      // --- FILTRAGE ET DEPLACEMENT DES BONUS ---
      bonuses = bonuses.filter(bns => {
        bns.x -= gameSpeed;

        if (bns.x + bns.width < 0) return false;

        if (bns.sprite?.complete) {
          ctx.drawImage(bns.sprite, bns.x, bns.y, bns.width, bns.height);
        }

        if (
          player.x < bns.x + bns.width &&
          player.x + player.width > bns.x &&
          currentHitboxY < bns.y + bns.height &&
          currentHitboxY + currentHitboxHeight > bns.y
        ) {
          currentScore += (activePower === 'Pouvoir : Score x2') ? 400 : 200;    
          nb_bonus++;
          activePower = bns.power; 
          timerActivePower = 0; // Remise à zéro pour réinitialiser le bandeau visuel
          
          setPulseBonus(true);
          setTimeout(() => setPulseBonus(false), 300);
          return false; 
        }
        return true;
      });

      // --- ENCADRÉ VISUEL DU POWER ACTIF ---
      if (activePower !== '') {
        ctx.save();
        
        const rectX = canvas.width - 210;
        const rectY = 15;
        const rectWidth = 190;  
        const rectHeight = 48;
        const cornerSize = 8; 

        let themeColor = '#fffd56'; 
        let bonusSprite = null;

        if (activePower === 'Pouvoir : Saut double') {
          bonusSprite = imagesRef.current.usb;
        } else if (activePower === 'Pouvoir : Score x2') {
          bonusSprite = imagesRef.current.router;
        } else if (activePower === 'Pouvoir : Bouclier') {
          bonusSprite = imagesRef.current.shield;
        }

        ctx.beginPath();
        ctx.moveTo(rectX, rectY);
        ctx.lineTo(rectX + rectWidth - cornerSize, rectY);
        ctx.lineTo(rectX + rectWidth, rectY + cornerSize); 
        ctx.lineTo(rectX + rectWidth, rectY + rectHeight);
        ctx.lineTo(rectX, rectY + rectHeight);
        ctx.closePath();

        ctx.fillStyle = 'rgba(15, 23, 42, 0.85)';
        ctx.fill();
        ctx.shadowBlur = 10;
        ctx.shadowColor = themeColor;
        ctx.strokeStyle = themeColor;
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.shadowBlur = 0; 

        if (bonusSprite && bonusSprite.complete) {
          ctx.drawImage(bonusSprite, rectX + 10, rectY + 6, 24, 24);
        }

        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 10px sans-serif'; 
        ctx.textAlign = 'left';
        ctx.fillText(activePower.toUpperCase(), rectX + 40, rectY + 22);

        const timeLeftRatio = (300 - timerActivePower) / 300;
        const barMaxWidth = rectWidth - 20;
        const barWidth = barMaxWidth * timeLeftRatio;
        const barX = rectX + 10;
        const barY = rectY + 36;

        ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.fillRect(barX, barY, barMaxWidth, 4);

        ctx.save();
        ctx.shadowBlur = 6;
        ctx.shadowColor = themeColor;
        ctx.fillStyle = themeColor;
        ctx.fillRect(barX, barY, barWidth, 4);
        ctx.restore();

        ctx.restore();
      }
    };

    // Premier appel initial de la boucle avec passage implicite du timestamp
    animationFrameId = requestAnimationFrame(gameLoop);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('mousedown', handleMouseDown);
      cancelAnimationFrame(animationFrameId);
      clearInterval(uiUpdateInterval);
    };
  }, [onGameOver]);

  return (
    <div className="game-container">
      <div className="game-header">
        <div className={`bonus-counter ${pulseBonus ? 'pulse' : ''}`}>
          <svg viewBox="0 0 24 24" className="svg-header-icon cyan">
            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>BONUS : <strong>{nbBonus}</strong></span>
        </div>

        <div className="personal-record-board">
          <svg viewBox="0 0 24 24" className="svg-header-icon gold">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>RECORD : <strong>{String(personalRecord).padStart(6, '0')}</strong></span>
        </div>

        <div className="score-board">
          <svg viewBox="0 0 24 24" className="svg-header-icon lime">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>SCORE : {String(score).padStart(6, '0')}</span>
        </div>
      </div>

      <div className="canvas-wrapper">
        <canvas ref={canvasRef} width={800} height={300} className="game-canvas" />
        <video 
          ref={videoRef} 
          src="/assets/background.mp4" 
          loop 
          muted 
          playsInline 
          style={{ display: 'none' }} 
        />
      </div>

      <p className="controls-tip">
        Appuie sur <strong>ESPACE ou ↑</strong> pour sauter et <strong>↓</strong> pour glisser / faire des roulades
      </p>
    </div>
  );
};

export default GameCanva;